import { PivotControlType } from "./pivot_control_options";
import { DatePivotParams } from "./date_pivot_params";
import { Theme } from "@mui/material/styles";
import React from "react";
import * as arrow from "apache-arrow";
import type { CommonCellContextV2 } from "./forecast_cell_context";
import type { CommonMetricContext } from "./forecast_cell_context";
import type { Run } from "../services/appsync/types";
import type {
  CommonViewContextV1,
  CommonViewSurfaceId,
} from "./common_view_context";
import type { NucleusExplainActivation } from "./nucleus_explain_action";

export type RenderedViewBinding = Readonly<{
  /** Opaque identity for the exact accepted transform response. */
  generation: string;
  /** Run whose settings produced the rendered data. */
  run: Run;
  /** Immutable receipt for the exact accepted matrix bytes, when available. */
  receiptId?: string;
}>;

export const renderedViewBindingIdentity = (
  source: RenderedViewBinding,
): string => JSON.stringify([source.generation, source.receiptId ?? null]);

export type CellExplanationDraft = Readonly<{
  prompt: string;
  metricContext: CommonMetricContext;
  viewContext?: CommonViewContextV1;
}>;

export type CellExplanationCapture = CellExplanationDraft &
  Readonly<{ source: RenderedViewBinding }>;

export type TableCellExplanationCandidate = Readonly<{
  columnKey: string;
  row: Readonly<Record<string, unknown>>;
  rowIndex: number;
  value: unknown;
}>;

export type BoundCellExplanationConfig = Readonly<{
  capture: (
    candidate: TableCellExplanationCandidate,
  ) => CellExplanationCapture | undefined;
  onExplain: (capture: CellExplanationCapture) => NucleusExplainActivation;
  /** Keeps the exact cell action visible but disabled during another Nucleus turn. */
  disabled?: boolean;
}>;

export type PivotCellExplanationConfig<T extends DatePivotParams> = Readonly<{
  capture: (
    candidate: TableCellExplanationCandidate &
      Readonly<{ viewParams: T; source?: RenderedViewBinding }>,
  ) => CellExplanationDraft | undefined;
  onExplain: (capture: CellExplanationCapture) => NucleusExplainActivation;
}>;

export interface PivotViewConfig<T extends DatePivotParams = DatePivotParams> {
  datasetName: string;
  transformId: string;
  folder: string;
  title: string;
  /** Publishes bounded active controls for descriptive typed-chat context. */
  viewContextSurfaceId?: CommonViewSurfaceId;
  noteText?: string;
  dateHierarchyColumnFilter: Record<string, string> | null;
  excludedSegmentationsFilter: string[];
  additionalSegmentationColumns?: { name: string; caption: string }[];
  measureColumnFilter:
    | Record<string, string>
    | ((params: T) => Record<string, string>)
    | null;
  excludedFilterColumnFilter: string[];
  showControls: PivotControlType[];
  hardcodedColumns?: {
    name: string;
    caption: string;
    unique_values: string[];
  }[];
  paramsToTransformArgs?: (params: T) => Record<string, any>;
  showFirstTableRow?: boolean;
  tableType?: "pivot" | "flat";
  columnHeaderMap?: Record<string, string>;
  columnFormatters?: Record<string, (value: any, row: any) => React.ReactNode>;
  shadeHistory?: boolean;
  // Detail/breakdown label cells render without the action.hover fill, so
  // only date columns carry shading. Unset = legacy grey detail labels.
  plainDetailLabels?: boolean;
  // Measure picker offers the measureColumnFilter keys directly instead of
  // intersecting with dataset columns. For matrix-style views whose measures
  // are computed rows (ratios) that never exist as dataset columns.
  measureOptionsFromFilterKeys?: boolean;
  showHeaderRow?: boolean;
  customControlPanel?: React.ComponentType<{
    viewControllerConfig: PivotViewConfig<T>;
    params: T;
    setParams: (params: T) => void;
    isLoading: boolean;
    isChartView?: boolean;
    setIsChartView?: React.Dispatch<React.SetStateAction<boolean>>;
    sortConfig: Array<{
      key: string;
      direction: "asc" | "desc";
      index: number;
    }>;
    setSortConfig: React.Dispatch<
      React.SetStateAction<
        Array<{ key: string; direction: "asc" | "desc"; index: number }>
      >
    >;
    dateHierarchyColumns: {
      name: string;
      caption: string;
      min: string;
      max: string;
    }[];
    segmentColumns: { name: string; caption: string }[];
    measureColumns: { name: string; caption: string }[];
    filterColumns: { name: string; caption: string; unique_values: string[] }[];
    columnSchema: Record<
      string,
      { dtype: string; min?: string; max?: string; unique_values?: string[] }
    >;
    parsedData?: any;
  }>;
  demandWalkConfig?: {
    plannerOverrideInputs: Record<string, Record<string, string>>;
    finalForecastInputs: Record<string, Record<string, string>>;
    yoyPercentageInputs: Record<string, Record<string, string>>;
    setPlannerOverrideInputs: React.Dispatch<
      React.SetStateAction<Record<string, Record<string, string>>>
    >;
    setFinalForecastInputs: React.Dispatch<
      React.SetStateAction<Record<string, Record<string, string>>>
    >;
    setYoYPercentageInputs: React.Dispatch<
      React.SetStateAction<Record<string, Record<string, string>>>
    >;
    editMode?: boolean;
    /** Demand Plan route only: render persisted-override markers + hover from
     *  the demand_plan_editable view columns. Unset for the Beta Demand Walk. */
    showPersistedOverrideMarkers?: boolean;
  };
  purchaseOrderConfig?: {
    getInputs: (field: string) => Record<string, string>;
    setInputs: (
      field: string,
      updater: React.SetStateAction<Record<string, string>>,
    ) => void;
    editableFields: string[];
  };
  purchasingCalendarConfig?: {
    deliveryDateEdits: import("../contexts/purchasing_calendar_edits/type").DeliveryDateEdits;
    setDeliveryDateEdits: React.Dispatch<
      React.SetStateAction<
        import("../contexts/purchasing_calendar_edits/type").DeliveryDateEdits
      >
    >;
    editMode?: boolean;
  };
  excludeDetailRows?: boolean;
  /** Additional measure names that render as indented sub-rows (breakdown rows) in the chunked matrix. */
  breakdownMeasures?: string[];
  /** Additional measure names that should show a TOTAL column in the chunked matrix. */
  additionalMeasuresWithTotals?: string[];
  /**
   * Make "open"/"simulated" PO-arrival breakdown cells clickable and mount the
   * per-cell PO detail popover (fetches po_cell_details over po_plan_edit).
   */
  enablePoCellPopover?: boolean;
  /**
   * Make "transfer order to place" cells clickable and mount the per-cell
   * transfer detail popover (fetches transfer_cell_details over
   * supply_walk_test_spoke): per source hub, the hub on-hand and the spoke
   * arrival date. Gated to the Spokes view.
   */
  enableTransferCellPopover?: boolean;
  /**
   * Make "unfulfilled demand" cells clickable and mount the per-cell demand
   * source popover (fetches unfulfilled_demand_details over supply_walk_test):
   * sales by channel, plus transfers by destination on hub cells. The
   * per-source split is an ESTIMATE allocated by demand share — the popover
   * labels it as such; see the transform's README.
   */
  enableUnfulfilledDemandPopover?: boolean;
  /**
   * Make "forecasted sales" cells clickable and mount the per-cell sales
   * source popover (fetches forecasted_sales_details over supply_walk_test):
   * sales by channel — retailers, on Cora — plus transfers by destination on
   * hub cells. Every figure is exact (summed from the simulation), unlike the
   * unfulfilled-demand split. Units and share only; revenue is deliberately
   * not shown (COR-77).
   */
  enableForecastedSalesPopover?: boolean;
  /**
   * Make Demand ACTUALS cells clickable and mount the per-cell sales-order
   * popover (fetches sales_order_details over sales_order_master_daily): the
   * orders behind a quantity, narrowed by the view's active segments. Forecast
   * cells have no underlying orders and stay plain. Generic pivot path only.
   */
  enableSalesOrderPopover?: boolean;
  /**
   * Render the ❯ ACTUALS / FORECAST strip on chunked date-matrix views whose
   * range spans the active run's as-of date (split is run-relative, not
   * today-relative). No-op when every visible column is post-as-of.
   */
  showActualsForecastStrip?: boolean;
  /** When set, chunked path (URL + rowCount) will re-request with this transform ID to get matrix-shaped parquet. */
  chunkedMatrixTransformId?: string;
  /**
   * Optional long-form transform used only to obtain an immutable receipt for
   * the same run and controls when the rendered matrix transform is wide.
   */
  receiptTransformId?: string;
  /** Optional generic boundary for a route to capture and explain supported business cells. */
  cellExplanation?: PivotCellExplanationConfig<T>;
  /** Keeps exact source binding active while visibly disabling busy actions. */
  cellExplanationEnabled?: boolean;
  /** Invalidates deferred cell intent when a new request/source replaces the rendered rows. */
  onRenderedSourceChange?: (
    source: RenderedViewBinding | undefined,
    viewParams?: T,
  ) => void;
  /** Optional, surface-owned action for supported Forecast Tracking cells. */
  onForecastCellExplain?: (
    context: CommonCellContextV2,
    source: RenderedViewBinding,
    viewContext?: CommonViewContextV1,
  ) => NucleusExplainActivation;
  getMatrix?: (args: {
    arrowTable: arrow.Table;
    columns: arrow.Field<any>[];
    viewParams: T;
    demandWalkConfig?: PivotViewConfig<T>["demandWalkConfig"];
    purchaseOrderConfig?: PivotViewConfig<T>["purchaseOrderConfig"];
    purchasingCalendarConfig?: PivotViewConfig<T>["purchasingCalendarConfig"];
    sortConfig: Array<{
      key: string;
      direction: "asc" | "desc";
      index: number;
    }>;
    setSortConfig: React.Dispatch<
      React.SetStateAction<
        Array<{ key: string; direction: "asc" | "desc"; index: number }>
      >
    >;
    onSort: (key: string, e: React.MouseEvent<HTMLTableCellElement>) => void;
    columnSchema: Record<
      string,
      { dtype: string; min?: string; max?: string; unique_values?: string[] }
    >;
    theme: Theme;
    expandedGroups: Set<string>;
    setExpandedGroups: React.Dispatch<React.SetStateAction<Set<string>>>;
    columnHeaderMap?: Record<string, string>;
    firstMount: React.MutableRefObject<boolean>;
    onForecastCellExplain?: (
      context: CommonCellContextV2,
    ) => NucleusExplainActivation;
    cellExplanationDisabled?: boolean;
  }) => {
    columns: any[];
    getRow: (rowIndex: number) => Record<string, any>;
    rowCount: number;
    columnTotals?:
      | Record<string, number>
      | Array<{ tableId: string; totals: Record<string, number> }>;
  } | null;
}
